// Engagement model context + the "Engine details" sub-layout. The Workspace 2 layout (pages/ws/Workspace.tsx) loads the
// engagement once through <EngagementProvider>; the earlier workspace tabs (status, flow, file, evidence, planning, areas,
// single audit, completion, client threads, agents, health, release) live on under /e/:id/details/*, where engine internals
// (record head, seq, lease) may be shown. Tab pages read the loaded model through useEngagement().
import { createContext, useContext, type ReactNode } from "react";
import { Link, Outlet } from "react-router-dom";
import { useApp } from "../../app/context";
import { ENGAGEMENT_TABS } from "../../app/nav";
import { ErrorState, LinkTabs, LoadingRows } from "../../components";
import type { EngagementModel } from "../../data/types";
import { ago } from "../../lib/format";
import { useLoad, useRealtime } from "../../lib/hooks";

interface EngCtx { id: string; model: EngagementModel; reload: () => void }
const Ctx = createContext<EngCtx | null>(null);
export function useEngagement(): EngCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useEngagement outside the engagement workspace");
  return c;
}

/** Loads the engagement model once and keeps it fresh (Realtime); children read it with useEngagement(). */
export function EngagementProvider({ id, children }: { id: string; children: ReactNode }) {
  const { ds } = useApp();
  const m = useLoad(() => ds.engagement(id), [ds, id]);
  useRealtime(ds, "actions", (e) => { if (!e.engagementId || e.engagementId === id) m.reload(); });
  if (m.error) return <ErrorState error={m.error} onRetry={m.reload} />;
  if (!m.data) return <LoadingRows rows={6} label="Loading the engagement" />;
  return <Ctx.Provider value={{ id, model: m.data, reload: m.reload }}>{children}</Ctx.Provider>;
}

/** The earlier workspace tab (or workpaper page) under "Engine details". */
export const detailsPath = (id: string, tab = "") => `/e/${id}/details${tab ? "/" + tab : ""}`;

/** "Engine details": the earlier workspace tabs with the engine's own vocabulary, one click away from every screen. */
export function EngagementLayout() {
  const { id, model } = useEngagement();
  const h = model.header;
  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <div className="text-xs text-muted"><Link to={`/e/${id}`} className="hover:underline">{h.clientName}</Link> /</div>
        <h1 className="text-xl font-semibold">Engine details</h1>
        <p className="break-words text-sm text-muted">
          What the engine records for this engagement, in its own words. Record head <span className="font-mono" title={h.auditHead ?? undefined}>{h.auditHead ? `${h.auditHead.slice(0, 12)}…` : "—"}</span>
          {h.auditSeq !== null ? ` (seq ${h.auditSeq})` : ""} · synced {ago(h.syncedAt)}{h.lease ? ` · engine: ${h.lease.holderName}` : " · no engine holds the lease"}
        </p>
      </header>
      <LinkTabs label="Engine details sections" tabs={ENGAGEMENT_TABS.map((t) => ({ to: detailsPath(id, t.path), label: t.label, end: !t.path }))} />
      <Outlet />
    </div>
  );
}
